"use client";

import { useState } from "react";
import { Badge } from "@workspace/ui/components/badge";
import { Input } from "@workspace/ui/components/input";
import { X } from "lucide-react";

interface ListFieldProps {
  value?: string[];
  editing?: boolean;
  onChange?: (items: string[]) => void;
  placeholder?: string;
}

const STACK_THRESHOLD = 24;

function shouldStack(items: string[]): boolean {
  return items.some((s) => s.length > STACK_THRESHOLD);
}

export function ListField({
  value = [],
  editing,
  onChange,
  placeholder = "Add item + Enter",
}: ListFieldProps) {
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const item = inputValue.trim();
      if (!value.includes(item)) {
        onChange?.([...value, item]);
      }
      setInputValue("");
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      onChange?.(value.slice(0, -1));
    }
  };

  const stack = shouldStack(value);

  if (!editing) {
    if (value.length === 0) {
      return <span className="text-sm text-muted-foreground italic">—</span>;
    }
    if (stack) {
      return (
        <ul className="space-y-1 list-disc list-inside text-sm">
          {value.map((item) => (
            <li key={item} className="leading-snug">
              {item}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((item) => (
          <Badge
            key={item}
            variant="outline"
            className="text-sm px-2 py-0.5 font-normal whitespace-normal break-words"
          >
            {item}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {stack ? (
        <ul className="space-y-1 text-sm">
          {value.map((item) => (
            <li key={item} className="flex items-start gap-1.5 leading-snug">
              <span className="mt-1 h-1 w-1 rounded-full bg-muted-foreground/60 shrink-0" />
              <span className="flex-1 min-w-0 break-words">{item}</span>
              <button
                type="button"
                onClick={() => onChange?.(value.filter((i) => i !== item))}
                className="rounded-sm hover:bg-foreground/10 p-0.5 shrink-0"
                aria-label="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-wrap gap-1">
          {value.map((item) => (
            <Badge
              key={item}
              variant="outline"
              className="text-sm px-2 py-0.5 font-normal gap-1 whitespace-normal break-words"
            >
              {item}
              <button
                type="button"
                onClick={() => onChange?.(value.filter((i) => i !== item))}
                className="rounded-sm hover:bg-foreground/10"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="h-8 text-sm px-2"
      />
    </div>
  );
}
